import * as THREE from 'three';
import { emis, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Raigón (the Chasm's Buried Wood guardian, docs/SIMA.md «Guardianes»): «Cuando hunde las raíces, salí del
 * círculo.» An old trunk of the forest that sank into the shell's crack centuries ago, walking: a gnarled stump
 * of black bark as wide as a cart, split at its top into a crown of twisting roots; a face of knots and hollows
 * where moss-green light burns; two branch-arms ending in root claws that drag on the ground; legs of twisted
 * root bundles splaying into toes of root. Moss crusts its shoulders, green light seeps from the cracks in its
 * bark, stones are caught in its roots.
 *
 * How the integrator drives it (anim/creatures/rootking.ts): clip 'plunge' (3.0 s) is the rule: it rears with
 * both claws high (0.7 s), drives them into the ground (0.95 s) and the circle of roots bursts up round it
 * (the `ring` bone, built tiny, grows from 0.9 s to full at 1.3 s), squeezes, and lets go at 2.6 s. 'rooted' is
 * the hold as a loop (claws in the ground, the ring up), for a hold longer than the clip's.
 *
 * Bones: body (hips), chest (the trunk), head (the face and the crown), arms in three (arm, fore, hand), legs
 * (leg, shin) and `ring` (the circle of roots, at the ground, built at ROOT_RING_REST of its size; `rooted`
 * builds it full size, for the gallery). Faces +Z.
 */

/** How small the circle of roots is built (the plunge clip scales `ring` by 1 / ROOT_RING_REST). */
export const ROOT_RING_REST = 0.02;
export const ROOTKING_HEIGHT = 2.75;

export const rootking = (o?: BaseOpts & { rooted?: boolean }): Model => {
  const c = begin(o, 1.2, 5150);
  const { g, rng } = c;
  const k4 = kit(rng);

  const bark = std('#2b241f', { roughness: 0.95, flatShading: true, bumpScale: 0.07 });
  const barkL = std('#4a4038', { roughness: 0.9, flatShading: true, bumpScale: 0.06 });
  const root = std('#1d1915', { roughness: 0.9, bumpScale: 0.05 });
  const moss = std('#3c5524', { roughness: 1, flatShading: true, bumpScale: 0.08 });
  const light = emis('#c4f29e', '#7fc25a', 1.6);
  const rock = stone('#4a4c4f');

  /** A gnarled trunk section: a tapered cylinder with its rings pushed in and out. */
  const trunk = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r1: number, r2: number, mat: THREE.Material, seg = 9): THREE.Mesh => {
    const m = limb(parent, a, b, r1, r2, mat, seg);
    const geo = m.geometry as THREE.CylinderGeometry;
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const seen = new Map<string, number>();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const key = `${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}`;
      let d = seen.get(key);
      if (d === undefined) {
        d = 1 + (rng() - 0.5) * 0.32;
        seen.set(key, d);
      }
      pos.setXYZ(i, x * d, y, z * d);
    }
    geo.computeVertexNormals();
    m.rotateY(rng() * 6.3);
    return m;
  };
  /** A root: a tapering wavy tube from `a` toward `b`, ending in a point. */
  const rootLine = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, r: number, mat: THREE.Material = root, wob = 0.08): void => {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 3; i++) pts.push(a.clone().lerp(b, i / 3).add(i && i < 3 ? V((rng() - 0.5) * wob, (rng() - 0.5) * wob, (rng() - 0.5) * wob) : V(0, 0, 0)));
    k4.tube(parent, pts.slice(0, 3), r, mat, 4);
    const tip = M(new THREE.ConeGeometry(r * 0.9, pts[2]!.distanceTo(pts[3]!) * 1.3, 5), mat, parent);
    tip.position.copy(pts[2]!).lerp(pts[3]!, 0.6);
    tip.quaternion.setFromUnitVectors(V(0, 1, 0), pts[3]!.clone().sub(pts[2]!).normalize());
  };
  /** A seam of green light in the bark. */
  const seep = (parent: THREE.Object3D, pts: THREE.Vector3[]): void => {
    k4.crack(parent, pts, 0.016, light, 0.05);
  };

  /* ---------- legs: twisted root bundles, splaying into root toes ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = joint(g, V(k * 0.38, 1.0, 0), `leg${s}`);
    const hip = legB.abs;
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      k4.tube(hip, [V(k * 0.38 + Math.cos(a) * 0.12, 1.08, Math.sin(a) * 0.12), V(k * 0.42 + Math.cos(a + 1) * 0.13, 0.8, Math.sin(a + 1) * 0.12), V(k * 0.44 + Math.cos(a + 2) * 0.11, 0.56, 0.04 + Math.sin(a + 2) * 0.11)], 0.11, i ? root : bark, 6);
    }
    k4.lump(hip, moss, k * 0.44, 1.0, 0.1, 0.16, 0.07, 0.14, 0.4, 0);
    const shinB = joint(hip, V(k * 0.44, 0.58, 0.04), `shin${s}`);
    const lo = shinB.abs;
    trunk(lo, V(k * 0.44, 0.62, 0.04), V(k * 0.48, 0.16, 0.08), 0.2, 0.26, bark, 7);
    seep(lo, [V(k * 0.5, 0.52, 0.24), V(k * 0.53, 0.38, 0.27), V(k * 0.5, 0.24, 0.3)]);
    // Root toes, splayed on the ground, one dragging behind.
    for (let i = 0; i < 5; i++) {
      const a = -0.9 + i * 0.45 + (k > 0 ? 0.15 : -0.15);
      const end = V(k * 0.48 + Math.sin(a) * 0.55, 0.02, 0.08 + Math.cos(a) * 0.55);
      rootLine(lo, V(k * 0.48, 0.2, 0.1), end, 0.07 - Math.abs(i - 2) * 0.008);
    }
    rootLine(lo, V(k * 0.48, 0.2, -0.05), V(k * 0.6, 0.02, -0.55), 0.06);
    k4.lump(lo, rock, k * 0.62, 0.1, 0.32, 0.1, 0.08, 0.1, 0.6, 0);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips ---------- */
  const bodyB = joint(g, V(0, 1.05, 0), 'body');
  const body = bodyB.abs;
  trunk(body, V(0, 0.92, 0), V(0, 1.3, 0), 0.6, 0.55, bark, 10);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    k4.tube(body, [V(Math.cos(a) * 0.5, 1.2, Math.sin(a) * 0.45), V(Math.cos(a) * 0.64, 1.0, Math.sin(a) * 0.55), V(Math.cos(a) * 0.6, 0.86, Math.sin(a) * 0.5)], 0.07, root, 5);
  }

  /* ---------- chest: the great trunk, leaning a little forward, moss on its shoulders ---------- */
  const chestB = joint(body, V(0, 1.28, 0), 'chest');
  const chest = chestB.abs;
  trunk(chest, V(0, 1.24, 0), V(0, 2.05, 0.08), 0.6, 0.7, bark, 10);
  trunk(chest, V(0, 1.5, -0.1), V(0, 2.0, -0.05), 0.55, 0.62, barkL, 8);
  // Bark ridges running up it.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const r1 = 0.62, r2 = 0.72;
    k4.tube(chest, [V(Math.cos(a) * r1, 1.3, Math.sin(a) * r1), V(Math.cos(a + 0.1) * (r1 + 0.05), 1.65, Math.sin(a + 0.1) * (r1 + 0.05) + 0.03), V(Math.cos(a + 0.05) * r2, 2.0, Math.sin(a + 0.05) * r2 + 0.07)], 0.045, i % 2 ? barkL : bark, 4);
  }
  // Moss over the shoulders and down the back, light seeping from the cracks.
  for (const k of [-1, 1]) {
    k4.lump(chest, moss, k * 0.52, 2.0, -0.05, 0.36, 0.14, 0.42, 0.4, 1);
    k4.lump(chest, moss, k * 0.3, 1.75, -0.5, 0.25, 0.3, 0.12, 0.3, 0);
    seep(chest, [V(k * 0.25, 1.35, 0.62), V(k * 0.3, 1.55, 0.66), V(k * 0.22, 1.78, 0.72)]);
    seep(chest, [V(k * 0.62, 1.4, 0.2), V(k * 0.68, 1.62, 0.25), V(k * 0.64, 1.85, 0.32)]);
  }
  seep(chest, [V(0, 1.32, 0.64), V(0.05, 1.5, 0.68), V(-0.04, 1.66, 0.72)]);
  glowSprite(chest, '#7fc25a', 0.9, V(0, 1.5, 0.75), 0.25);
  k4.lump(chest, rock, -0.45, 1.45, 0.52, 0.13, 0.11, 0.1, 0.5, 0);

  /* ---------- head: the upper trunk with its face of knots, split at the top into the crown of roots ---------- */
  const headB = joint(chest, V(0, 2.02, 0.1), 'head');
  const head = headB.abs;
  trunk(head, V(0, 1.9, 0.1), V(0, 2.42, 0.06), 0.6, 0.48, bark, 10);
  // Eye hollows under a heavy brow slanting down to the middle, the green fire deep in them, narrowed.
  for (const sd of [-1, 1]) {
    const brow = M(new THREE.BoxGeometry(0.26, 0.07, 0.14), barkL, head);
    brow.position.set(sd * 0.17, 2.3, 0.68);
    brow.rotation.set(0.2, 0, sd * 0.32);
    const hol = M(new THREE.SphereGeometry(0.1, 8, 6), root, head);
    hol.position.set(sd * 0.18, 2.2, 0.65);
    hol.scale.set(1.15, 0.6, 0.5);
    const eye = M(new THREE.SphereGeometry(0.03, 6, 4), light, head);
    eye.position.set(sd * 0.17, 2.19, 0.69);
    eye.scale.set(1.5, 0.6, 1);
    glowSprite(head, '#a9f07a', 0.22, V(sd * 0.17, 2.19, 0.74), 0.85);
  }
  // The mouth: a split knothole gaping under them, splinters for teeth.
  const mouth = M(new THREE.SphereGeometry(0.17, 8, 6), root, head);
  mouth.position.set(0, 2.02, 0.66);
  mouth.scale.set(1.2, 0.7, 0.5);
  for (let i = 0; i < 5; i++) {
    const sp = M(new THREE.ConeGeometry(0.022, 0.11, 4), barkL, head);
    sp.position.set(-0.13 + i * 0.065, 2.08, 0.72);
    sp.rotation.x = Math.PI;
    const lo = M(new THREE.ConeGeometry(0.02, 0.08, 4), barkL, head);
    lo.position.set(-0.1 + i * 0.05, 1.96, 0.72);
  }
  glowSprite(head, '#7fc25a', 0.4, V(0, 2.0, 0.66), 0.4);
  // Splintered top of the trunk, then the crown: roots twisting up and out.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const sp = M(new THREE.ConeGeometry(0.09, 0.26 + rng() * 0.18, 4), barkL, head);
    sp.position.set(Math.cos(a) * 0.36, 2.48, 0.06 + Math.sin(a) * 0.36);
    sp.rotation.set(Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3);
  }
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.2;
    const r0 = 0.3;
    const out = 0.32 + rng() * 0.18, up = 0.55 + rng() * 0.35 - (Math.sin(a) < -0.3 ? 0.15 : 0);
    const p0 = V(Math.cos(a) * r0, 2.4, 0.06 + Math.sin(a) * r0);
    const p1 = V(Math.cos(a) * (r0 + out * 0.4), 2.4 + up * 0.55, 0.06 + Math.sin(a) * (r0 + out * 0.4));
    const p2 = V(Math.cos(a + 0.3) * (r0 + out), 2.4 + up, 0.06 + Math.sin(a + 0.3) * (r0 + out));
    const p3 = V(Math.cos(a + 0.6) * (r0 + out * 1.25), 2.4 + up * 0.92, 0.06 + Math.sin(a + 0.6) * (r0 + out * 1.25));
    k4.tube(head, [p0, p1, p2], 0.06, i % 3 ? root : bark, 4);
    const tip = M(new THREE.ConeGeometry(0.05, 0.22, 5), root, head);
    tip.position.copy(p2).lerp(p3, 0.5);
    tip.quaternion.setFromUnitVectors(V(0, 1, 0), p3.clone().sub(p2).normalize());
  }
  // Moss hanging from the crown, glowing at its tips.
  for (let i = 0; i < 4; i++) {
    const a = 0.6 + i * 1.5;
    const at = V(Math.cos(a) * 0.5, 2.62, 0.06 + Math.sin(a) * 0.5);
    k4.tube(head, [at, at.clone().add(V(0.02, -0.18, 0.02)), at.clone().add(V(-0.02, -0.36, 0.03))], 0.02, moss, 4);
    M(new THREE.SphereGeometry(0.025, 5, 3), light, head).position.copy(at).add(V(-0.02, -0.38, 0.03));
  }

  /* ---------- arms: branches to the knees, root claws dragging the ground ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(chest, V(k * 0.68, 1.95, 0.02), `arm${s}`);
    const sh = armB.abs;
    k4.lump(sh, bark, k * 0.72, 1.98, 0.02, 0.26, 0.24, 0.26, 0.4, 1);
    trunk(sh, V(k * 0.76, 1.92, 0.02), V(k * 1.02, 1.35, 0.1), 0.2, 0.16, bark, 7);
    // A dead twig off the elbow.
    rootLine(sh, V(k * 0.95, 1.5, 0.0), V(k * 1.3, 1.85, -0.2), 0.035, barkL, 0.05);
    const foreB = joint(sh, V(k * 1.02, 1.35, 0.1), `fore${s}`);
    const fo = foreB.abs;
    k4.lump(fo, barkL, k * 1.02, 1.35, 0.1, 0.17, 0.16, 0.17, 0.4, 0);
    trunk(fo, V(k * 1.02, 1.35, 0.1), V(k * 1.08, 0.78, 0.24), 0.16, 0.2, bark, 7);
    k4.lump(fo, moss, k * 1.1, 1.18, 0.12, 0.12, 0.16, 0.12, 0.4, 0);
    seep(fo, [V(k * 1.12, 1.25, 0.26), V(k * 1.14, 1.05, 0.3), V(k * 1.12, 0.88, 0.34)]);
    const handB = joint(fo, V(k * 1.08, 0.78, 0.24), `hand${s}`);
    const ha = handB.abs;
    k4.lump(ha, bark, k * 1.08, 0.72, 0.26, 0.2, 0.16, 0.2, 0.4, 0);
    // Root claws: five long roots reaching down and forward.
    for (let i = 0; i < 5; i++) {
      const a = -0.8 + i * 0.4;
      rootLine(ha, V(k * 1.08 + Math.sin(a) * 0.08, 0.66, 0.3 + Math.cos(a) * 0.05), V(k * 1.08 + Math.sin(a) * 0.32, 0.12 + Math.abs(i - 2) * 0.06, 0.44 + Math.cos(a) * 0.22), 0.045, i % 2 ? root : bark, 0.06);
    }
    k4.lump(ha, rock, k * 1.15, 0.6, 0.38, 0.08, 0.07, 0.08, 0.5, 0);
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
    arms[`hand${s}`] = handB.p;
  }

  /* ---------- the circle of roots (built tiny; the plunge clip grows it) ---------- */
  const ringB = joint(g, V(0, 0, 0), 'ring');
  const rg = G(ringB.abs);
  // (Built full size only for the gallery's look at the rule.)
  rg.scale.setScalar(o?.rooted ? 1 : ROOT_RING_REST);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const r = 1.55 + (i % 2) * 0.15;
    const base = V(Math.cos(a) * r, -0.05, Math.sin(a) * r);
    const h = 0.75 + (i % 3) * 0.18;
    // Each root bursts up and arches in over the circle.
    const p1 = V(Math.cos(a) * (r + 0.05), h * 0.55, Math.sin(a) * (r + 0.05));
    const p2 = V(Math.cos(a + 0.08) * (r - 0.25), h, Math.sin(a + 0.08) * (r - 0.25));
    const p3 = V(Math.cos(a + 0.12) * (r - 0.55), h * 0.85, Math.sin(a + 0.12) * (r - 0.55));
    k4.tube(rg, [base, p1, p2], 0.09, i % 2 ? root : bark, 4);
    const tip = M(new THREE.ConeGeometry(0.08, 0.36, 5), root, rg);
    tip.position.copy(p2).lerp(p3, 0.5);
    tip.quaternion.setFromUnitVectors(V(0, 1, 0), p3.clone().sub(p2).normalize());
    k4.lump(rg, i % 3 ? rock : moss, base.x * 1.04, 0.06, base.z * 1.04, 0.16, 0.08, 0.16, 0.5, 0);
    if (i % 2 === 0) seep(rg, [base.clone().setY(0.05), p1.clone().multiplyScalar(0.98)]);
  }
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    glowSprite(rg, '#7fc25a', 0.9, V(Math.cos(a) * 1.55, 0.25, Math.sin(a) * 1.55), 0.3);
  }

  return done(c, ROOTKING_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p,
    armL: arms.armL!, foreL: arms.foreL!, handL: arms.handL!, armR: arms.armR!, foreR: arms.foreR!, handR: arms.handR!,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!, ring: ringB.p,
  }));
};
